import { Component, type ReactNode } from "react";

import { Button } from "../components/Button";
import { EmptyState } from "../components/EmptyState";

type RouteBoundaryProps = {
  readonly children: ReactNode;
};

type RouteBoundaryState = {
  readonly error: Error | null;
};

// A runtime error shows a designed recovery, never a blank screen.
export class RouteBoundary extends Component<
  RouteBoundaryProps,
  RouteBoundaryState
> {
  state: RouteBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): RouteBoundaryState {
    return { error };
  }

  private readonly reset = (): void => this.setState({ error: null });

  private readonly reload = (): void => window.location.reload();

  render(): ReactNode {
    const { error } = this.state;
    if (!error) {
      return this.props.children;
    }
    return (
      <EmptyState
        action={
          <>
            <div style={{ display: "flex", gap: 8 }}>
              <Button onClick={this.reset} size="sm">
                Try again
              </Button>
              <Button onClick={this.reload} size="sm" variant="accent">
                Reload
              </Button>
            </div>
            <div
              className="mono"
              style={{
                color: "var(--ink-4)",
                fontSize: 11,
                marginTop: 14,
                maxWidth: "48ch",
              }}
            >
              {String(error).slice(0, 140)}
            </div>
          </>
        }
        body="This view hit an unexpected error. Your agents keep running — reload to reconnect."
        markSize={64}
        style={{ paddingTop: 96 }}
        title="The seam split."
      />
    );
  }
}
